import { FormattedMessage, useIntl } from 'react-intl';

import { getLabel } from '@/utils/labelUtils';

import { ProductCard } from '@/components/ProductCard';
import { getProductCardImageAlt } from '@/components/ProductCard/helpers';
import type { OrderAppCtaType } from '@/graphql/orderApp/api';
import type { EyewearHit } from '@/integrations/algolia/types';
import type { ProductCatalogModeCtaStyle } from '@/lib/types';
import { theme } from '@/theme';

import { useProductListCardCommon } from './helper';

interface EyewearCardProps {
  cardClickHandler?: () => void;
  hideFavorite?: boolean;
  hit: EyewearHit;
  priority?: boolean;
  productCatalogModeCtaStyle?: ProductCatalogModeCtaStyle;
  sizes?: string;
  style?: React.CSSProperties;
  type?: string;
}

export const EyewearCard = ({
  cardClickHandler,
  hideFavorite = false,
  hit,
  priority = false,
  productCatalogModeCtaStyle = 'DISCOVER',
  sizes,
  style,
  type,
}: EyewearCardProps) => {
  const { formatMessage } = useIntl();
  const {
    contextProductCatalogMode,
    detailPath,
    handleClickEvent,
    imageUrl,
    isLocalProcessing,
    isProcessing,
    name,
  } = useProductListCardCommon(hit);

  if (!hit) return null;
  const label = getLabel(hit.marketing_status, hit.launch_date);

  return (
    <ProductCard
      backgroundColor={theme.colors.lightGrey}
      cardClickHandler={cardClickHandler}
      cta={
        (!contextProductCatalogMode ||
          productCatalogModeCtaStyle !== 'HIDDEN') && (
          <ProductCard.CTA
            cta={
              contextProductCatalogMode
                ? 'DISCOVER'
                : (hit.call_to_action as OrderAppCtaType)
            }
            data-test-id='EyewearCardProductCardCTA'
            disabled={isProcessing}
            isProcessing={isLocalProcessing}
            onClick={handleClickEvent}
          />
        )
      }
      ctaType='bottom'
      data-test-id='EyewearCardProductCard'
      href={detailPath}
      sku={hit.objectID}
      style={style}
      topSlot={
        !hideFavorite && (
          <ProductCard.Favorite
            data-test-id='EyewearCardProductCardFavorite'
            name={name}
            price={hit.price}
            sku={hit.objectID}
            theCurrency={hit.currency}
            type={hit.product_type}
          />
        )
      }
      type={hit.product_type}
    >
      <ProductCard.Zone>
        <ProductCard.Image
          alt={getProductCardImageAlt({
            formatMessage,
            name,
            sku: hit.objectID,
          })}
          className={`${
            type === 'square-eyewear' ? 'square-eyewear' : 'eyewear'
          } eyewear-card`}
          data-test-id='EyewearCardProductCardImage'
          loading={priority ? 'eager' : 'lazy'}
          priority={priority}
          productType='eyewear'
          sizes={sizes ?? '(max-width: 600px) 50vw, 25vw'}
          src={imageUrl ?? ''}
          withBackgroundPlaceholderBackground={false}
        />
      </ProductCard.Zone>
      <ProductCard.Zone>
        <ProductCard.CardZoneLabelPriceRefGroup>
          <ProductCard.Sku data-test-id='EyewearCardProductCardSku'>
            {hit.objectID}
          </ProductCard.Sku>
          <ProductCard.Name data-test-id='EyewearCardProductCardName'>
            {name}
          </ProductCard.Name>
          {!!hit.price && (
            <ProductCard.Price
              data-test-id='EyewearCardProductCardPrice'
              price={{ amount: hit.price, currency: hit.currency }}
            />
          )}
          {label && (
            <ProductCard.Label
              data-test-id='EyewearCardProductCardLabel'
              text={<FormattedMessage {...label} />}
            />
          )}
        </ProductCard.CardZoneLabelPriceRefGroup>
      </ProductCard.Zone>
    </ProductCard>
  );
};
